import React from 'react';
import {
  Filter, UserPlus, Edit, LogIn, FileText,
  CheckCircle, XCircle, PauseCircle, Trash2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const payMethodLabel = (m) => {
  if (!m) return '';
  return m === 'upi' ? 'UPI' : m === 'bank_transfer' ? 'Bank' : m.charAt(0).toUpperCase() + m.slice(1);
};

export const GuestManagementTab = ({
  filteredBookings, statusFilter, setStatusFilter,
  setShowAddGuestDialog, generateInvoice, openEditDialog,
  openCheckoutDialog, updateBookingStatus, deleteBooking
}) => {
  return (
    <div data-testid="bookings-content">
      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><Filter size={16} className="mr-2" /><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="checked_out">Checked Out</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowAddGuestDialog(true)} className="bg-[#D4AF37] hover:bg-[#E5C766]"><UserPlus size={16} className="mr-2" /> Add Guest</Button>
      </div>
      <div className="bg-[#121816] rounded-lg border border-[#D4AF37]/30 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Guest</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Rooms</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Advance</TableHead>
              <TableHead>Check-in Coll. 1</TableHead>
              <TableHead>Check-in Coll. 2</TableHead>
              <TableHead>Restaurant</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredBookings.map(booking => {
                const roomsDisplay = (booking.room_names && booking.room_names.length > 0)
                  ? booking.room_names.join(', ')
                  : (booking.room_name || booking.room_type);
                return (
                  <TableRow key={booking.id}>
                    <TableCell className="font-medium">{booking.guest_name}</TableCell>
                    <TableCell><div className="text-sm"><p>{booking.phone}</p>{booking.email && <p className="text-xs text-[#8E9A95] truncate max-w-[150px]">{booking.email}</p>}</div></TableCell>
                    <TableCell>
                      <div className="text-sm capitalize">{roomsDisplay}</div>
                      <div className="text-xs text-[#8E9A95]">{booking.num_guests} guests</div>
                    </TableCell>
                    <TableCell><div className="text-sm"><p>{fmtDate(booking.check_in_date)}</p><p className="text-[#8E9A95]">to {fmtDate(booking.check_out_date)}</p></div></TableCell>
                    <TableCell>₹{(booking.total_price || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <span className="text-green-600">₹{(booking.advance_amount || 0).toLocaleString()}</span>
                      {booking.advance_payment_method && <span className="block text-xs text-[#8E9A95]">{payMethodLabel(booking.advance_payment_method)}</span>}
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600">₹{(booking.checkin_collection || 0).toLocaleString()}</span>
                      {booking.checkin_payment_method && (booking.checkin_collection || 0) > 0 && <span className="block text-xs text-[#8E9A95]">{payMethodLabel(booking.checkin_payment_method)}</span>}
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600">₹{(booking.checkin_collection_2 || 0).toLocaleString()}</span>
                      {booking.checkin_collection_2_method && (booking.checkin_collection_2 || 0) > 0 && <span className="block text-xs text-[#8E9A95]">{payMethodLabel(booking.checkin_collection_2_method)}</span>}
                    </TableCell>
                    <TableCell>
                      <span className="text-purple-600">₹{(booking.restaurant_bill || 0).toLocaleString()}</span>
                      {booking.restaurant_bill_method && (booking.restaurant_bill || 0) > 0 && <span className="block text-xs text-[#8E9A95]">{payMethodLabel(booking.restaurant_bill_method)}</span>}
                    </TableCell>
                    <TableCell className="text-[#D4AF37] font-medium">₹{(booking.balance || 0).toLocaleString()}</TableCell>
                    <TableCell><span className={`status-badge status-${booking.status}`}>{booking.status?.replace('_', ' ')}</span></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => generateInvoice(booking)} className="text-purple-600" title="Invoice"><FileText size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(booking)} className="text-blue-600" title="Edit"><Edit size={16} /></Button>
                        {booking.status === 'confirmed' && <Button size="sm" variant="ghost" onClick={() => openCheckoutDialog(booking)} className="text-[#D4AF37]" title="Checkout"><LogIn size={16} /></Button>}
                        {booking.status === 'pending' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(booking.id, 'confirmed')} className="text-green-600" title="Confirm"><CheckCircle size={16} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(booking.id, 'on_hold')} className="text-yellow-600" title="Hold"><PauseCircle size={16} /></Button>
                          </>
                        )}
                        {booking.status === 'on_hold' && <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(booking.id, 'confirmed')} className="text-green-600" title="Confirm"><CheckCircle size={16} /></Button>}
                        <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(booking.id, 'cancelled')} className="text-red-400" title="Cancel"><XCircle size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteBooking(booking.id)} className="text-[#8E9A95]" title="Delete"><Trash2 size={16} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
